import { useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import { AlertCircle, CheckCircle2, FileWarning, Upload, XCircle } from 'lucide-react'
import clsx from 'clsx'
import type { TestIdentificationResult } from '@/api/testIdentification'
import { InfoTooltip } from '@/components/InfoTooltip'
import { PDF_ACCEPT } from '../constants'

export interface TestPanelProps {
  onTest: (pdfBase64: string) => void
  isPending: boolean
  result: TestIdentificationResult | null
  /** Set when the mutation itself failed (a real HTTP error) - distinct from
   * any outcome in `result`, per Frontend PRD 03 Core logic step 7. */
  error: string | null
}

// Reads a File as base64, stripping the "data:...;base64," prefix - the
// backend's pdf_base64 field is bare base64 (PRD 25), not a data URL.
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const commaIndex = result.indexOf(',')
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

// The design brief's single highest-value screen element - an elevated
// surface-raised card (vs. the static form above it) so it reads as
// interactive, not just another form section (Frontend PRD 03 AC2).
export function TestPanel({ onTest, isPending, result, error }: TestPanelProps) {
  const [fileName, setFileName] = useState<string | null>(null)
  const [pdfBase64, setPdfBase64] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleFile(file: File) {
    setFileName(file.name)
    setPdfBase64(await readFileAsBase64(file))
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (file) void handleFile(file)
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setIsDragOver(false)
    const file = event.dataTransfer.files?.[0]
    if (file) void handleFile(file)
  }

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface-raised p-4 shadow-[0_0_0_1px_rgba(76,141,255,0.15)]">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Test this configuration
          <InfoTooltip text="Upload a real PDF to see how it would be identified with your current unsaved settings, before saving." />
        </h2>
      </div>

      <div
        onDragOver={(event) => {
          event.preventDefault()
          setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={clsx(
          'flex cursor-pointer flex-col items-center gap-2 rounded-control border border-dashed p-6 text-center',
          isDragOver ? 'border-accent bg-accent/10' : 'border-border',
        )}
      >
        <Upload size={20} strokeWidth={1.5} className="text-text-secondary" />
        <span className="text-body text-text-primary">
          {fileName ?? 'Drag & drop a PDF, or click to choose a file'}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={PDF_ACCEPT}
          className="hidden"
          onChange={handleInputChange}
        />
      </div>

      <button
        type="button"
        disabled={!pdfBase64 || isPending}
        onClick={() => pdfBase64 && onTest(pdfBase64)}
        className="w-fit rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isPending ? 'Testing…' : 'Test'}
      </button>

      <TestResult result={result} error={error} isPending={isPending} />
    </section>
  )
}

function TestResult({
  result,
  error,
  isPending,
}: {
  result: TestIdentificationResult | null
  error: string | null
  isPending: boolean
}) {
  if (isPending) {
    return <p className="text-meta text-text-secondary">Running the identification engine…</p>
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-control border border-border bg-surface px-3 py-2 text-body text-text-secondary">
        <AlertCircle size={16} strokeWidth={1.5} className="shrink-0 text-text-secondary" />
        {error}
      </div>
    )
  }

  if (!result) {
    return <p className="text-meta text-text-disabled">Upload a PDF and click Test to see a result here.</p>
  }

  if (result.outcome === 'no_text_layer') {
    return (
      <div className="flex items-start gap-2 rounded-control border border-warning/40 bg-warning/10 px-3 py-2 text-body text-warning">
        <FileWarning size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" />
        <span>
          No readable text could be extracted from this PDF, even after OCR fallback. This is a
          document-quality issue, not a keyword configuration problem. Try a clearer scan.
        </span>
      </div>
    )
  }

  const isConfirmed = result.outcome === 'confirmed'
  const failingGroups = result.groupHit
    .map((hit, index) => (hit ? null : index + 1))
    .filter((group): group is number => group !== null)

  return (
    <div
      className={clsx(
        'flex flex-col gap-2 rounded-control border px-3 py-2',
        isConfirmed ? 'border-success/40 bg-success/10' : 'border-danger/40 bg-danger/10',
      )}
    >
      <div className={clsx('flex items-center gap-2 text-body font-medium', isConfirmed ? 'text-success' : 'text-danger')}>
        {isConfirmed ? (
          <CheckCircle2 size={16} strokeWidth={1.5} />
        ) : (
          <XCircle size={16} strokeWidth={1.5} />
        )}
        {isConfirmed ? 'Confirmed match' : 'No match'}
      </div>
      {!isConfirmed && failingGroups.length > 0 && (
        <p className="text-meta text-danger">
          No match found in {failingGroups.length === 1 ? 'group' : 'groups'}{' '}
          {failingGroups.join(', ')}.
        </p>
      )}
      {result.textSource === 'ocr' && (
        <span className="inline-flex w-fit items-center rounded-pill bg-surface px-2 py-1 text-meta text-text-secondary">
          Text extracted via OCR fallback
        </span>
      )}
    </div>
  )
}
